import { useRef, useEffect, useState } from "react";
import QRCode from "qrcode";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import type { SavedQRCode } from "@/hooks/useQRCodes";
import { toast } from "sonner";

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const REDIRECT_BASE = "https://getxohype.com/r";

export type DownloadFormat = "png" | "jpeg" | "svg" | "pdf";

interface Props {
  qr: SavedQRCode;
  onClose: () => void;
}

export default function DownloadModal({ qr, onClose }: Props) {
  const [format, setFormat] = useState<DownloadFormat>("png");
  const [downloading, setDownloading] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const previewRef = useRef<HTMLDivElement>(null);

  const redirectUrl = `${REDIRECT_BASE}?code=${qr.shortCode}`;

  // Generate high-res QR image
  useEffect(() => {
    QRCode.toDataURL(redirectUrl, {
      width: 600,
      margin: 2,
      color: { dark: qr.fgColor, light: qr.bgColor },
      errorCorrectionLevel: qr.errorLevel,
    })
      .then((url) => setQrImageUrl(url))
      .catch(() => {});
  }, [redirectUrl, qr.fgColor, qr.bgColor, qr.errorLevel]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (format === "svg") {
        const svgStr = await QRCode.toString(redirectUrl, {
          type: "svg",
          color: { dark: qr.fgColor, light: qr.bgColor },
          errorCorrectionLevel: qr.errorLevel,
        });
        const blob = new Blob([svgStr], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${qr.name}.svg`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("SVG indirildi!");
      } else if (format === "pdf") {
        if (!previewRef.current) return;
        const dataUrl = await toPng(previewRef.current, { quality: 1, pixelRatio: 2 });
        const pdf = new jsPDF({ unit: "mm", format: "a4" });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const imgWidth = 100;
        const x = (pageWidth - imgWidth) / 2;
        const y = 50;
        pdf.addImage(dataUrl, "PNG", x, y, imgWidth, imgWidth);
        pdf.setFontSize(14);
        pdf.text(qr.name, pageWidth / 2, y + imgWidth + 12, { align: "center" });
        if (qr.description) {
          pdf.setFontSize(9);
          pdf.text(qr.description, pageWidth / 2, y + imgWidth + 20, { align: "center" });
        }
        pdf.setFontSize(8);
        pdf.text(`Hedef: ${qr.targetUrl}`, pageWidth / 2, y + imgWidth + 28, { align: "center" });
        pdf.save(`${qr.name}.pdf`);
        toast.success("PDF indirildi!");
      } else {
        if (!previewRef.current) return;
        const dataUrl = await toPng(previewRef.current, { quality: 0.95, pixelRatio: 2 });
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `${qr.name}.${format}`;
        a.click();
        toast.success(`${format.toUpperCase()} indirildi!`);
      }
    } catch {
      toast.error("İndirme başarısız oldu");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900">QR Kodu İndir</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Preview */}
        <div
          ref={previewRef}
          className="w-full aspect-square rounded-xl overflow-hidden mb-5 bg-gray-50 flex items-center justify-center"
        >
          {qrImageUrl ? (
            <div className="flex flex-col items-center justify-center w-full h-full" style={{ backgroundColor: qr.bgColor }}>
              <img src={qrImageUrl} alt="QR" className="rounded-lg" style={{ width: 240, height: 240 }} />
              {qr.name && (
                <p className="text-sm font-semibold mt-3" style={{ color: qr.fgColor }}>
                  {qr.name}
                </p>
              )}
              <p className="text-xs mt-1" style={{ color: qr.fgColor, opacity: 0.5 }}>
                Taramak için karekodu okutun
              </p>
            </div>
          ) : (
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-300"></i>
          )}
        </div>

        {/* Format selector */}
        <label className="block text-xs font-medium text-slate-500 mb-2">Format</label>
        <div className="grid grid-cols-4 gap-2 mb-5">
          {(["png", "svg", "jpeg", "pdf"] as DownloadFormat[]).map((f) => (
            <button
              key={f}
              onClick={() => setFormat(f)}
              className={`py-2 rounded-lg text-xs font-bold uppercase transition-all cursor-pointer whitespace-nowrap ${
                format === f
                  ? "bg-blue-600 text-white"
                  : "bg-slate-50 text-slate-500 hover:bg-slate-100"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Info */}
        <div className="bg-slate-50 rounded-lg px-3 py-2 mb-4 text-xs text-slate-500">
          <p>QR içinde: <span className="font-mono text-slate-700">{redirectUrl}</span></p>
          <p className="mt-0.5">Hedef: <span className="text-slate-700">{qr.targetUrl}</span></p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-full hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading || !qrImageUrl}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
          >
            {downloading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                İndiriliyor...
              </>
            ) : (
              <>
                <i className="ri-download-2-line"></i>
                İndir
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}